import {
  buildCompanyBusinessLinks,
  buildCompanySocialLinks,
  CompanyLink,
} from "@/lib/company-social-links";
import { cn } from "@/lib/utils";
import { CompanyDTO } from "@shaarp/common";
import { RiGlobalLine } from "react-icons/ri";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

type Props = {
  company: CompanyDTO;
  t: (key: string, opts?: Record<string, unknown>) => string;
};

/**
 * Inline company links displayed under the company name.
 * Small ghost icons in muted color with hover:text-primary.
 */
export function CompanyInlineLinks({ company, t }: Props): React.ReactElement | null {
  const businessLinks = buildCompanyBusinessLinks(company);
  const socialLinks = buildCompanySocialLinks(company);
  const websiteLink: CompanyLink | null = company.website
    ? {
        icon: RiGlobalLine,
        label: t("components.dataTableCompanies.links.website", {
          domain: company.domain,
        }),
        url: company.website,
        colorClass: "",
      }
    : null;

  const allLinks = [
    ...businessLinks,
    ...(websiteLink ? [websiteLink] : []),
    ...socialLinks,
  ];

  if (allLinks.length === 0) return null;

  return (
    <div className="flex items-center gap-1 flex-wrap">
      <TooltipProvider>
        {allLinks.map((link) => (
          <Tooltip key={link.url}>
            <TooltipTrigger asChild>
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "hover:opacity-70 transition-opacity",
                  link.colorClass || "text-muted-foreground",
                )}
              >
                <link.icon className="size-3.5" />
              </a>
            </TooltipTrigger>
            <TooltipContent side="bottom">{link.label}</TooltipContent>
          </Tooltip>
        ))}
      </TooltipProvider>
    </div>
  );
}
